import { useRouter } from 'next/router'
import { Product } from '@mc/api'
import { useBogo } from '@mc/bogo-utils'
import {
  useBundlingToolkitAndCertificatesExperiment,
  useCertsAddOnExperiment,
  useMCProExperiment,
  usePlanZFreeGiftExperiment,
} from '@mc/checkout'
import type { Section } from '@mc/faq'
import { useFreeTrial } from '@mc/free-trial'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { getBogoSection } from '../data/getBogoSection'
import { getFreeTrialSection } from '../data/getFreeTrialSection'
import { getGeneralSection } from '../data/getGeneralSection'
import { getGiftingFaqSection } from '../data/getGiftingSection'
import { getGuestPassSection } from '../data/getGuestPassSection'
import { getPricingSection } from '../data/getPricingSection'

export type SectionsOptions = Array<
  | 'general'
  | 'pricing'
  | 'bogo'
  | 'promo'
  | 'free-trial'
  | 'guest-pass'
  | 'gifting'
>

export const useFaqData = ({
  sections = ['general', 'pricing'],
}: {
  sections?: SectionsOptions
}) => {
  const { pathname } = useRouter()
  const isCheckoutPage = pathname.startsWith(paths.checkout)

  const { annualPass } = useStaticData<{ annualPass: Product }>()
  const { isBogoOfferEligible } = useBogo()
  const { trial } = useFreeTrial()

  const { isVariant: isBundlingToolkitAndCertificates } =
    useBundlingToolkitAndCertificatesExperiment()
  const { isVariant: isPlanZFreeGift } = usePlanZFreeGiftExperiment()
  const { isEligible: isCertsAddOn } = useCertsAddOnExperiment()
  const { isVariant: isMCPro } = useMCProExperiment()

  const generalSection = getGeneralSection({
    isBundlingToolkitAndCertificates:
      isBundlingToolkitAndCertificates && isCheckoutPage,
    isPlanZFreeGift: isPlanZFreeGift && isCheckoutPage,
    isCertsAddOn: isCertsAddOn && isCheckoutPage, // prcng-1138_v1
    isMCPro: isMCPro && isCheckoutPage, // prcng-1194_v1
  })
  const pricingSection = getPricingSection({
    isCheckoutPage,
    annualPass,
  })
  const bogoSection = getBogoSection()
  const guestPassSection = getGuestPassSection({
    trial,
  })
  const freeTrialSection = getFreeTrialSection({ trial })
  const giftSection = getGiftingFaqSection({
    isBundlingToolkitAndCertificates:
      isBundlingToolkitAndCertificates && isCheckoutPage, // prcng-698_v1
  })

  const faqData: Section[] = []
  if (sections?.length) {
    sections.forEach((section) => {
      if (section === 'general') faqData.push(generalSection)
      if (section === 'pricing') faqData.push(pricingSection)
      if (section === 'bogo' && isBogoOfferEligible) faqData.push(bogoSection)
      if (section === 'guest-pass' && trial.isGuestPass)
        faqData.push(guestPassSection)
      if (section === 'free-trial' && trial.isPartnershipTrial)
        faqData.push(freeTrialSection)
      if (section === 'gifting') faqData.push(giftSection)
    })
  }

  return { faqData }
}
